/**
 * API Service for XLR8 Admin Console
 *
 * Handles all communication with the Django backend API
 * for beta user management and admin console functionality.
 */
import axios, { AxiosResponse } from "axios";
import {
  BetaUser,
  InviteToken,
  BetaUserActivity,
  AdminDashboard,
  CreateBetaUserForm,
  GenerateInviteForm,
  TableFilters,
} from "../types";

// Configure base API URL
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:8000/api";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send httpOnly cookies with every request
  headers: {
    "Content-Type": "application/json",
  },
});

// NOTE: Request interceptor removed - authentication now handled by httpOnly cookies
// Cookies are automatically sent with every request via withCredentials: true
// No need to manually inject Authorization headers

// Add response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we get a 401 error and haven't already tried to refresh the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        console.log("Attempting cookie-based token refresh...");
        // Call refresh endpoint - it reads refresh token from httpOnly cookie
        // and sets a new access token cookie in the response
        await axios.post(
          `${API_BASE_URL}/users/auth/refresh/`,
          {}, // Empty body - refresh token is in httpOnly cookie
          { withCredentials: true }, // Send cookies with this request
        );

        console.log("Token refreshed successfully, retrying original request");
        // New access token cookie is now set, retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);

        // Clear user data from localStorage (cookies cleared by backend)
        localStorage.removeItem("xlr8_user");

        // Only redirect if we're not already on a login/onboarding/admin page
        // Admin console (/daddyslink) has its own password protection
        if (
          !window.location.pathname.includes("/slinkup") &&
          !window.location.pathname.includes("/login") &&
          !window.location.pathname.includes("/daddyslink")
        ) {
          console.log("Redirecting to login due to authentication failure");
          window.location.href = "/slinkup/login";
        }

        return Promise.reject(refreshError);
      }
    }

    console.error("API Error:", error.response?.data || error.message);
    return Promise.reject(error);
  },
);

// API Service Class
export class AdminApiService {
  // =============================================================================
  // DASHBOARD API
  // =============================================================================

  static async getDashboard(): Promise<AdminDashboard> {
    const response: AxiosResponse<AdminDashboard> = await apiClient.get(
      "/beta/admin/dashboard/",
    );
    return response.data;
  }

  // =============================================================================
  // BETA USERS API
  // =============================================================================

  static async getBetaUsers(filters: TableFilters = {}): Promise<BetaUser[]> {
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if (filters.role) params.append("role", filters.role);
    if (filters.familiarity)
      params.append("familiarity", filters.familiarity.toString());
    if (filters.invited !== undefined)
      params.append("invited", filters.invited.toString());

    const response: AxiosResponse<BetaUser[]> = await apiClient.get(
      `/beta/admin/users/?${params.toString()}`,
    );
    return response.data; // Return all users (pagination disabled for admin console)
  }

  static async getBetaUser(id: string): Promise<BetaUser> {
    const response: AxiosResponse<BetaUser> = await apiClient.get(
      `/beta/admin/users/${id}/`,
    );
    return response.data;
  }

  static async createBetaUser(userData: CreateBetaUserForm): Promise<BetaUser> {
    const response: AxiosResponse<BetaUser> = await apiClient.post(
      "/beta/admin/users/",
      userData,
    );
    return response.data;
  }

  static async updateBetaUser(
    id: string,
    userData: Partial<CreateBetaUserForm>,
  ): Promise<BetaUser> {
    const response: AxiosResponse<BetaUser> = await apiClient.put(
      `/beta/admin/users/${id}/`,
      userData,
    );
    return response.data;
  }

  static async deleteBetaUser(id: string): Promise<void> {
    await apiClient.delete(`/beta/admin/users/${id}/`);
  }

  // =============================================================================
  // GUDGANG API (for Kayo & Wiz)
  // =============================================================================

  static async getGudGangStats(addedBy: string): Promise<{
    total_users: number;
    invited_users: number;
    completed_onboarding: number;
    in_progress: number;
    your_referrals: number;
  }> {
    const response = await apiClient.get(
      `/beta/gudgang/stats/?added_by=${addedBy}`,
    );
    return response.data;
  }

  static async getGudGangUsers(
    addedBy: string,
    filters: TableFilters = {},
  ): Promise<BetaUser[]> {
    const params = new URLSearchParams();

    // Only add added_by filter if it's not empty (empty = show all users)
    if (addedBy) params.append("added_by", addedBy);

    if (filters.search) params.append("search", filters.search);
    if (filters.role) params.append("role", filters.role);
    if (filters.familiarity)
      params.append("familiarity", filters.familiarity.toString());

    const response: AxiosResponse<BetaUser[]> = await apiClient.get(
      `/beta/gudgang/users/?${params.toString()}`,
    );
    return response.data;
  }

  static async createGudGangUser(
    userData: CreateBetaUserForm & { added_by: string },
  ): Promise<BetaUser> {
    const response: AxiosResponse<BetaUser> = await apiClient.post(
      "/beta/gudgang/users/",
      userData,
    );
    return response.data;
  }

  // =============================================================================
  // INVITE TOKENS API
  // =============================================================================

  static async generateInviteToken(
    userId: string,
    options: GenerateInviteForm = { expires_in_days: 30 },
  ): Promise<InviteToken> {
    const response: AxiosResponse<InviteToken> = await apiClient.post(
      `/beta/admin/users/${userId}/generate-invite/`,
      options,
    );
    return response.data;
  }

  // =============================================================================
  // ACTIVITIES API
  // =============================================================================

  static async getBetaUserActivities(
    filters: TableFilters = {},
  ): Promise<BetaUserActivity[]> {
    const params = new URLSearchParams();

    if (filters.praised !== undefined)
      params.append("praised", filters.praised.toString());
    if (filters.search) params.append("user", filters.search); // assuming search is user ID

    const response: AxiosResponse<BetaUserActivity[]> = await apiClient.get(
      `/beta/admin/activities/?${params.toString()}`,
    );
    return response.data || []; // Return all activities (pagination disabled for admin console)
  }

  static async markActivityPraised(
    activityId: number,
  ): Promise<BetaUserActivity> {
    const response: AxiosResponse<{
      message: string;
      activity: BetaUserActivity;
    }> = await apiClient.post(
      `/beta/admin/activities/${activityId}/mark-praised/`,
    );
    return response.data.activity;
  }

  // =============================================================================
  // BETA APPLICATIONS API
  // =============================================================================

  static async getBetaApplications(filters: TableFilters = {}): Promise<any[]> {
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if (filters.status) params.append("status", filters.status);

    const response: AxiosResponse<any[]> = await apiClient.get(
      `/beta/admin/applications/?${params.toString()}`,
    );
    return response.data || []; // Return all applications (pagination disabled for admin console)
  }

  static async getBetaApplication(id: string): Promise<any> {
    const response: AxiosResponse<any> = await apiClient.get(
      `/beta/admin/applications/${id}/`,
    );
    return response.data;
  }

  static async approveApplication(id: string): Promise<any> {
    const response: AxiosResponse<any> = await apiClient.post(
      `/beta/admin/applications/${id}/approve/`,
    );
    return response.data;
  }

  static async rejectApplication(id: string, reason?: string): Promise<any> {
    const response: AxiosResponse<any> = await apiClient.post(
      `/beta/admin/applications/${id}/reject/`,
      {
        admin_notes: reason,
      },
    );
    return response.data;
  }

  // =============================================================================
  // USER FEEDBACK API
  // =============================================================================

  static async getUserFeedback(filters: TableFilters = {}): Promise<any[]> {
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if (filters.status) params.append("status", filters.status);

    const response: AxiosResponse<any[]> = await apiClient.get(
      `/beta/admin/feedback/?${params.toString()}`,
    );
    return response.data || []; // Return all feedback (pagination disabled for admin console)
  }

  static async getFeedbackDetail(id: string): Promise<any> {
    const response: AxiosResponse<any> = await apiClient.get(
      `/beta/admin/feedback/${id}/`,
    );
    return response.data;
  }

  static async markFeedbackReviewed(id: string): Promise<any> {
    const response: AxiosResponse<any> = await apiClient.post(
      `/beta/admin/feedback/${id}/review/`,
    );
    return response.data;
  }

  static async markFeedbackImplemented(id: string): Promise<any> {
    const response: AxiosResponse<any> = await apiClient.post(
      `/beta/admin/feedback/${id}/implement/`,
    );
    return response.data;
  }

  // =============================================================================
  // OWNERSHIP RECORDS API
  // =============================================================================

  static async getOwnershipRecords(filters: TableFilters = {}): Promise<any[]> {
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if ((filters as any).content_type)
      params.append("content_type", (filters as any).content_type);
    if ((filters as any).blockchain_verified)
      params.append(
        "blockchain_verified",
        (filters as any).blockchain_verified,
      );

    const response: AxiosResponse<any[]> = await apiClient.get(
      `/beta/admin/ownership-records/?${params.toString()}`,
    );
    return response.data || []; // Return all records (pagination disabled for admin console)
  }

  static async getOwnershipRecordDetail(id: string): Promise<any> {
    const response: AxiosResponse<any> = await apiClient.get(
      `/beta/admin/ownership-records/${id}/`,
    );
    return response.data;
  }

  // =============================================================================
  // MESSAGING API
  // =============================================================================

  static async getUnreadCount(): Promise<{ unread_count: number }> {
    const response = await apiClient.get("/collaborations/unread-count/");
    return response.data;
  }

  // =============================================================================
  // GENRES API
  // =============================================================================

  static async getGenres(): Promise<{ id: number; name: string }[]> {
    // Request all genres in one call (we have < 30 total)
    const response = await apiClient.get("/users/genres/?page_size=100");
    // API returns paginated response with results array
    return response.data.results || response.data;
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  static async healthCheck(): Promise<{
    status: string;
    timestamp: string;
    version: string;
  }> {
    const response = await apiClient.get("/beta/health/");
    return response.data;
  }

  // Generic API client methods for direct endpoint calls
  static get apiClient() {
    return apiClient;
  }

  // Helper method to handle API errors consistently
  static handleApiError(error: any): string {
    // Check for detail field (used by DRF ValidationError and auth errors)
    if (error.response?.data?.detail) {
      // detail can be a string or an array
      if (Array.isArray(error.response.data.detail)) {
        return error.response.data.detail[0];
      }
      return error.response.data.detail;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.message) {
      return error.message;
    }
    return "An unexpected error occurred";
  }
}

export default AdminApiService;
